import { useState, useMemo } from 'react';
import { Shield, Grid3x3, List, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { controlsLibraryData, ComplianceFramework, ControlStatus } from '@/data/mockData';

type ViewMode = 'grid' | 'list';

export const ControlsLibrary: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [frameworkFilter, setFrameworkFilter] = useState<ComplianceFramework | 'All'>('All');
  const [statusFilter, setStatusFilter] = useState<ControlStatus | 'All'>('All');

  const getStatusIcon = (status: ControlStatus) => {
    switch (status) {
      case 'Active':
        return <CheckCircle className="w-5 h-5 text-success-400" />;
      case 'Under Review':
        return <Clock className="w-5 h-5 text-warning-400" />;
      case 'Pending':
        return <AlertCircle className="w-5 h-5 text-primary-400" />;
      case 'Inactive':
        return <XCircle className="w-5 h-5 text-neutral-500" />;
    }
  };

  const getStatusColor = (status: ControlStatus) => {
    switch (status) {
      case 'Active':
        return 'text-success-400 bg-success-500/20 border-success-500/30';
      case 'Under Review':
        return 'text-warning-400 bg-warning-500/20 border-warning-500/30';
      case 'Pending':
        return 'text-primary-400 bg-primary-500/20 border-primary-500/30';
      case 'Inactive':
        return 'text-neutral-400 bg-neutral-500/20 border-neutral-500/30';
    }
  };

  const getEffectivenessColor = (effectiveness: number) => {
    if (effectiveness >= 90) return 'bg-success-500';
    if (effectiveness >= 80) return 'bg-primary-500';
    if (effectiveness >= 70) return 'bg-warning-500';
    return 'bg-danger-500';
  };

  const filteredControls = useMemo(() => {
    return controlsLibraryData.filter((control) => {
      const matchesFramework = 
        frameworkFilter === 'All' || 
        control.framework.includes(frameworkFilter);
      
      const matchesStatus = 
        statusFilter === 'All' || 
        control.status === statusFilter;

      return matchesFramework && matchesStatus;
    });
  }, [frameworkFilter, statusFilter]);

  const frameworks: Array<ComplianceFramework | 'All'> = ['All', 'SOC2', 'ISO27001', 'NIST', 'GDPR', 'DORA', 'Basel III'];

  return (
    <div className="space-y-6">
      <ComingSoonWatermark 
        pageKey="controls-library"
        icon={Shield}
        title="Controls Library Coming Soon"
        description="Comprehensive controls library with framework mappings, effectiveness tracking, and automated testing workflows will be available soon."
      />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center">
            <Shield className="w-8 h-8 text-primary-400 mr-3" />
            Controls Library
          </h1>
          <p className="text-neutral-400 mt-1">Manage security and compliance controls across frameworks</p>
        </div>
        
        {/* View Toggle */}
        <div className="flex items-center gap-2 bg-neutral-900 rounded-lg p-1 border border-neutral-800">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'grid' 
                ? 'bg-primary-500/20 text-primary-400' 
                : 'text-neutral-400 hover:text-white'
            }`}
            title="Grid View"
          >
            <Grid3x3 className="w-5 h-5" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded transition-colors ${
              viewMode === 'list' 
                ? 'bg-primary-500/20 text-primary-400' 
                : 'text-neutral-400 hover:text-white'
            }`}
            title="List View"
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Framework Tabs */}
      <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-2">
        <div className="flex flex-wrap gap-2">
          {frameworks.map((framework) => (
            <button
              key={framework}
              onClick={() => setFrameworkFilter(framework)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                frameworkFilter === framework
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'text-neutral-400 hover:text-white hover:bg-neutral-900'
              }`}
            >
              {framework}
              {framework !== 'All' && (
                <span className="ml-2 text-xs opacity-70">
                  ({controlsLibraryData.filter(c => c.framework.includes(framework as ComplianceFramework)).length})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-4">
        <label className="text-neutral-400 text-sm">Filter by Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ControlStatus | 'All')}
          className="px-4 py-2 bg-neutral-900 border border-neutral-700 rounded-lg text-white focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        >
          <option value="All">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Under Review">Under Review</option>
          <option value="Pending">Pending</option>
          <option value="Inactive">Inactive</option>
        </select>
        <span className="text-neutral-400 text-sm">
          {filteredControls.length} control{filteredControls.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Controls Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredControls.map((control) => (
            <div 
              key={control.control_id}
              className="bg-neutral-950 rounded-lg border border-neutral-800 p-6 hover:border-primary-500/30 transition-all cursor-pointer group"
              onClick={() => console.log('View control:', control.control_id)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  {getStatusIcon(control.status)}
                  <span className="font-mono text-sm text-primary-400">{control.control_id}</span>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(control.status)}`}>
                  {control.status}
                </span>
              </div>

              {/* Name */}
              <h3 className="text-white font-semibold mb-2 group-hover:text-primary-400 transition-colors">
                {control.name}
              </h3>

              {/* Description */}
              <p className="text-neutral-400 text-sm mb-4 line-clamp-2">
                {control.description}
              </p>

              {/* Frameworks */}
              <div className="flex flex-wrap gap-1 mb-4">
                {control.framework.map((fw) => (
                  <span 
                    key={fw}
                    className="px-2 py-0.5 bg-neutral-900 text-neutral-300 text-xs rounded border border-neutral-700"
                  >
                    {fw}
                  </span>
                ))}
              </div>

              {/* Effectiveness */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-400">Effectiveness</span>
                  <span className="text-white font-medium">{control.effectiveness}%</span>
                </div>
                <div className="w-full bg-neutral-800 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all ${getEffectivenessColor(control.effectiveness)}`}
                    style={{ width: `${control.effectiveness}%` }}
                  />
                </div>
              </div>

              {/* Owner & Review */}
              <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center justify-between text-xs text-neutral-500">
                <span>Owner: {control.owner}</span>
                <span>Next Review: {control.next_review}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-900 border-b border-neutral-800">
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Control ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Name</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Frameworks</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Effectiveness</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Owner</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-neutral-300">Next Review</th>
              </tr>
            </thead>
            <tbody>
              {filteredControls.map((control) => (
                <tr 
                  key={control.control_id}
                  className="border-b border-neutral-800 hover:bg-neutral-900 transition-colors cursor-pointer"
                  onClick={() => console.log('View control:', control.control_id)}
                >
                  <td className="px-6 py-4">
                    <span className="font-mono text-sm text-primary-400">{control.control_id}</span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-white font-medium">{control.name}</p>
                    <p className="text-xs text-neutral-400 mt-1 line-clamp-1">{control.description}</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {control.framework.slice(0, 2).map((fw) => (
                        <span 
                          key={fw}
                          className="px-2 py-0.5 bg-neutral-900 text-neutral-300 text-xs rounded border border-neutral-700"
                        >
                          {fw}
                        </span>
                      ))}
                      {control.framework.length > 2 && (
                        <span className="px-2 py-0.5 text-neutral-500 text-xs">
                          +{control.framework.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(control.status)}
                      <span className="text-sm text-neutral-300">{control.status}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-neutral-800 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getEffectivenessColor(control.effectiveness)}`}
                          style={{ width: `${control.effectiveness}%` }}
                        />
                      </div>
                      <span className="text-sm text-white font-medium">{control.effectiveness}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-neutral-300">{control.owner}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-neutral-400">{control.next_review}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {filteredControls.length === 0 && (
        <div className="bg-neutral-950 rounded-lg border border-neutral-800 p-12 text-center">
          <Shield className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No controls found for the selected filters</p>
          <button 
            onClick={() => {
              setFrameworkFilter('All');
              setStatusFilter('All');
            }}
            className="mt-4 px-4 py-2 bg-primary-500/20 text-primary-400 border border-primary-500/30 rounded-lg hover:bg-primary-500/30 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      )}
    </div>
  );
};
